import {
	PropsWithChildren,
	ReactElement,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useState
} from "react";
import { AppImageDisplayInfo } from "client/Api/AppIcon/common";
import { AppType } from "client/Api/Apps/common";
import { AppTypeSpecificTileSettings } from "../../RenderState/common";
import { InteractionState, TileContext } from "../TileContext";
import { useAddAnalyticsAppEvent } from "client/Features/Analytics/useAddAnalyticsAppEvent";
import { LiveTileContent } from "../LiveTileContent";
import {
	GeneralIntegrationFlowConfiguration,
	GeneralIntegrationFlowResult,
	GeneralIntegrationFlowResultItemModel,
	GeneralIntegrationFlowLiveInformationResultStatus,
	ColoredValueModel,
	getFlowConfiguration,
	getLiveInformation
} from "client/Api/Workspace/LiveInformation/Integration/integrationLiveInformation";
import { IntegrationFlowMultipleAuthenticationsAreRequiredModel } from "client/Api/IntegrationBuilder/integrationBuilderAuthentication";
import { never } from "client_legacy/ndaw/errors";
import { Guid, newGuid } from "client_legacy/ndaw/guid";
import { hasImplicitDependencyOn } from "client/Misc/reactUtils";
import { isCancelledError } from "client_legacy/ndaw/cancellation";
import {
	BusyIndicator,
	ChangeTriggerKind,
	DateString,
	ListItem,
	PipeSeparator,
	SearchInputTrigger
} from "client/Components";
import { localize } from "client_legacy/ndaw/culture";
import {
	AuthenticateIntegrationFlowResultKind,
	useTryAuthenticateIntegrationFlow
} from "client/Features/Layout/TitleBar/GlobalSearch/useAuthenticateIntegrationFlow";
import {
	generateTrackingId,
	isNotNil,
	isNotNilOrWhiteSpace,
	setTrackingId
} from "client/Misc/utils";
import styles from "./IntegrationLiveTileContent.module.less";
import { TimeZone } from "client/DateAndTime/timezones";
import { ResultMapperColor } from "client/Api/IntegrationBuilder/integrationBuilderFlows";
import { useForceRerender } from "client/Hooks";
import { LiveTileOverlayState, useLiveTileOverlayState } from "../useLiveTileOverlayState";
import { Coordinate } from "client/Misc/commonTypes";
import { TileStatus } from "../TileStatus";

export type IntegrationTileSettings = AppTypeSpecificTileSettings<AppType.Integration>;

export type IntegrationLiveTileContentProps = {
	settings: IntegrationTileSettings;
	onNavigation: () => void;
	userTimezone: TimeZone;
	onOpenTileMenu: (coordinate: Coordinate) => void;
};

export const IntegrationLiveTileContent = (props: IntegrationLiveTileContentProps): ReactElement => {
	const {
		settings,
		onNavigation,
		userTimezone,
		onOpenTileMenu
	} = props;

	const {
		icon,
		name,
		environmentAppId,
		conditionalAccess,
		maintenanceWindow,
		interactionState,
		dimension,
		appIsLicensed,
		appIsNotAccessible
	} = useContext(TileContext);

	const overlayState: LiveTileOverlayState = useLiveTileOverlayState({
		interactionState,
		dimension,
		conditionalAccess,
		maintenanceWindow,
		navigationHandler: onNavigation,
		appIsLicensed,
		appIsNotAccessible
	});

	const isUnavailable: boolean = isNotNil(maintenanceWindow)
		|| isNotNil(conditionalAccess)
		|| interactionState === InteractionState.Disabled;

	if (isNotNil(maintenanceWindow) || isNotNil(conditionalAccess)) {
		return (
			<LiveTileContent
				icon={icon}
				name={name}
				onOpenTileMenu={onOpenTileMenu}
				overlayStatusIcon={<TileStatus />}
				overlay={overlayState}
			/>
		);
	}

	return (
		<IntegrationLiveTileContentInternal
			settings={settings}
			onNavigation={onNavigation}
			userTimezone={userTimezone}
			icon={icon}
			name={name}
			environmentAppId={environmentAppId}
			isUnavailable={isUnavailable}
			overlay={overlayState}
			onOpenTileMenu={onOpenTileMenu}
		/>
	);
};
IntegrationLiveTileContent.displayName = "IntegrationLiveTileContent";

type IntegrationLiveTileContentInternalProps = {
	settings: IntegrationTileSettings;
	onNavigation: () => void;
	userTimezone: TimeZone;
	icon: AppImageDisplayInfo;
	name: string;
	environmentAppId: number;
	isUnavailable: boolean;
	overlay: LiveTileOverlayState;
	onOpenTileMenu: (coordinate: Coordinate) => void;
};

const IntegrationLiveTileContentInternal = (props: IntegrationLiveTileContentInternalProps): ReactElement => {
	const {
		settings,
		onNavigation,
		userTimezone,
		icon,
		name,
		environmentAppId,
		isUnavailable,
		overlay,
		onOpenTileMenu
	} = props;

	const [state, setState] = useState<State>({
		status: "isLoading"
	});

	const [isAuthenticating, setIsAuthenticating] = useState<boolean>(false);

	const [searchTerm, setSearchTerm] = useState<VersionedValue<string>>({
		value: "",
		version: newGuid()
	});

	const [configuration, setConfiguration] = useState<ConfigurationState>({
		status: "isLoading"
	});

	const [reload, reloadVersion] = useForceRerender();

	const [authenticationDialogs, authenticate] = useTryAuthenticateIntegrationFlow();

	const {
		connectionId,
		hideHeader,
		hideFooter,
		navigationUrl
	} = settings;

	const hasSearchTerm: boolean = isNotNilOrWhiteSpace(searchTerm.value);

	const doesSupportSearch: boolean = configuration.status === "loaded"
		&& configuration.doesSupportSearch;

	const {
		addEvent: addAnalyticsEvent
	} = useAddAnalyticsAppEvent(environmentAppId);

	const navigateHandler = useCallback(
		(): void => {
			addAnalyticsEvent();
			onNavigation();
		},
		[addAnalyticsEvent, onNavigation]
	);

	const searchTermChangeHandler = useCallback(
		(newValue: string, version: Guid): void => {
			setSearchTerm({
				value: newValue,
				version
			});
		},
		[]
	);

	useEffect(
		(): (() => void) | undefined => {
			hasImplicitDependencyOn(reloadVersion);

			if (isUnavailable) {
				return undefined;
			}

			setConfiguration({
				status: "isLoading"
			});

			const cancellation = new AbortController();

			const load = async (): Promise<void> => {
				try {
					const result: GeneralIntegrationFlowConfiguration = await getFlowConfiguration(
						connectionId,
						cancellation.signal
					);

					setConfiguration({
						status: "loaded",
						doesSupportSearch: result.doesSupportSearch
					});
				} catch (e) {
					if (isCancelledError(e)) {
						return;
					}

					console.error(e);

					setConfiguration({
						status: "failed"
					});
				}
			};

			load();

			return (): void => {
				cancellation.abort();
			};
		},
		[
			reloadVersion,
			connectionId,
			isUnavailable
		]
	);

	useEffect(
		(): (() => void) | undefined => {
			if (isUnavailable || configuration.status !== "loaded") {
				return undefined;
			}

			if (configuration.doesSupportSearch && !hasSearchTerm) {
				return undefined;
			}

			setState({
				status: "isLoading"
			});

			const cancellation = new AbortController();

			const load = async (): Promise<void> => {
				try {
					const result: GeneralIntegrationFlowResult = await getLiveInformation(
						connectionId,
						{
							searchTerm: hasSearchTerm
								? {
									isSet: true,
									value: searchTerm.value
								}
								: { isSet: false }
						},
						cancellation.signal
					);

					switch (result.status) {
						case GeneralIntegrationFlowLiveInformationResultStatus.Failure:
							setState({
								status: "globalFailure"
							});
							break;
						case GeneralIntegrationFlowLiveInformationResultStatus.AuthenticationIsRequired:
							setState({
								status: "authenticationRequired",
								authenticationModel: result.authenticationModel
							});
							break;
						case GeneralIntegrationFlowLiveInformationResultStatus.Success:
							setState({
								status: "loaded",
								items: result.items.map(
									x => ({
										...x,
										uniqueId: newGuid()
									})
								)
							});
							break;
						default:
							never(result);
					}
				} catch (e) {
					if (isCancelledError(e)) {
						return;
					}

					console.error(e);

					setState({
						status: "globalFailure"
					});
				}
			};

			load();

			return (): void => {
				cancellation.abort();
			};
		},
		[
			searchTerm,
			hasSearchTerm,
			configuration,
			connectionId,
			isUnavailable
		]
	);

	const authenticateConfiguration = useMemo(
		(): { onRetry: () => void; retryText: string; } | undefined => {
			if (state.status !== "authenticationRequired") {
				return undefined;
			}

			const {
				authenticationModel,
				wasInvoked
			} = state;

			if (wasInvoked) {
				return {
					onRetry: reload,
					retryText: localize("_Finish")
				};
			}

			return {
				onRetry: async (): Promise<void> => {
					if (isAuthenticating) {
						return;
					}

					const {
						authentications: missingAuthentications
					} = authenticationModel;

					const result: TryResult<AuthenticateIntegrationFlowResultKind> = await authenticate(
						name,
						missingAuthentications,
						setIsAuthenticating
					);

					if (!result.wasSuccessful) {
						return;
					}

					switch (result.result) {
						case "completed":
							reload();
							break;
						case "wasInvoked":
							setState(x => {
								if (x.status !== "authenticationRequired") {
									return x;
								}

								return {
									...x,
									wasInvoked: true
								};
							});
							break;
						default:
							never(result.result);
					}
				},
				retryText: localize("_Authenticate")
			};
		},
		[
			state,
			name,
			isAuthenticating,
			reload,
			authenticate
		]
	);

	const renderGlobalFailure = (): ReactElement => (
		<LiveTileContent.NoResults
			icon="gear"
			title={localize("_UnableToRetrieveItems")}
			message={localize("_WeWereUnableToRetrieveTheItems")}
			onRetry={reload}
			trackingId="global-failure"
		/>
	);

	const renderFlowContent = (): ReactElement => {
		if (doesSupportSearch && !hasSearchTerm) {
			return (
				<LiveTileContent.NoResults
					icon="magnifying-glass"
					title={localize("_EnterASearchTerm")}
					message={localize("_PleaseEnterASearchTermToBeginYourSearch")}
					trackingId="search-term-required"
				/>
			);
		}

		switch (state.status) {
			case "globalFailure":
				return renderGlobalFailure();
			case "isLoading":
				return (<BusyIndicator />);
			case "authenticationRequired":
				return (
					<>
						<LiveTileContent.NoResults
							{...authenticateConfiguration}
							trackingId="authentication-required"
							icon="gear"
							title={localize("_AuthenticationRequired")}
							message={{
							template: localize("_ToUseNameAuthenticationIsNeeded"),
							replacements: [
									{ what: "{{Name}}", filler: name }
								]
							}}
						/>
						{isAuthenticating && <BusyIndicator />}
						{authenticationDialogs}
					</>
				);
			case "loaded":
				if (state.items.length === 0) {
					if (hasSearchTerm) {
						return (
							<LiveTileContent.NoResults
								icon="magnifying-glass"
								title={localize("_NoSearchResults")}
								message={localize("_PleaseTrySearchingWithAnotherTerm")}
								trackingId="no-search-results"
							/>
						);
					}

					return (
						<LiveTileContent.NoResults
							icon="list"
							title={localize("_NoItemsToShow")}
							message={localize("_ThereAreCurrentlyNoItemsToShowHere")}
							trackingId="no-items"
						/>
					);
				}

				return (
					<LiveTileContent.Scrollable>
						<List
							items={state.items}
							userTimezone={userTimezone}
						/>
					</LiveTileContent.Scrollable>
				);
			default:
				return never(state);
		}
	};

	const renderContent = (): ReactElement => {
		switch (configuration.status) {
			case "isLoading":
				return (<BusyIndicator />);
			case "failed":
				return renderGlobalFailure();
			case "loaded":
				return renderFlowContent();
			default:
				return never(configuration);
		}
	};

	return (
		<LiveTileContent
			name={name}
			icon={icon}
			overlay={overlay}
			onOpenTileMenu={onOpenTileMenu}
			overlayStatusIcon={<TileStatus />}
		>
			{!hideHeader && (
				<LiveTileContent.Header icon={icon}>
					<LiveTileContent.Title text={name} />
				</LiveTileContent.Header>
			)}

			<LiveTileContent.Content>
				<LiveTileContent.ContentStack>
					{doesSupportSearch && (
						<LiveTileContent.SearchInput
							value={searchTerm}
							onChange={searchTermChangeHandler}
							trigger={itemSearchTrigger}
							trackingId="search-input"
							placeholder={localize("_Search")}
						/>
					)}

					<LiveTileContent.BusyIndicatorContainer>
						{renderContent()}
					</LiveTileContent.BusyIndicatorContainer>
				</LiveTileContent.ContentStack>
			</LiveTileContent.Content>

			{navigationUrl && !hideFooter && (
				<LiveTileContent.Footer
					name={name}
					onClick={navigateHandler}
				/>
			)}
		</LiveTileContent>
	);
};
IntegrationLiveTileContentInternal.displayName = "IntegrationLiveTileContentInternal";

const itemSearchTrigger: SearchInputTrigger = { kind: ChangeTriggerKind.Debounced };

type ConfigurationState = {
	status: "isLoading" | "failed";
} | {
	status: "loaded";
	doesSupportSearch: boolean;
};

type State = {
	status: "globalFailure" | "isLoading";
} | {
	status: "authenticationRequired";
	authenticationModel: IntegrationFlowMultipleAuthenticationsAreRequiredModel;
	wasInvoked?: boolean;
} | {
	status: "loaded";
	items: readonly IntegrationTileItemModel[];
};

type IntegrationTileItemModel = GeneralIntegrationFlowResultItemModel & {
	uniqueId: Guid;
};

type ListProps = {
	items: readonly IntegrationTileItemModel[];
	userTimezone: TimeZone;
};

const List = (props: ListProps): ReactElement => {
	const {
		items,
		userTimezone
	} = props;

	return (
		<LiveTileContent.List>
			{items.map(item => (
				<li key={item.uniqueId}>
					<Item
						item={item}
						userTimezone={userTimezone}
					/>
				</li>
			))}
		</LiveTileContent.List>
	);
};
List.displayName = "List";

type ItemProps = {
	item: IntegrationTileItemModel;
	userTimezone: TimeZone;
};

const Item = (props: ItemProps): ReactElement => {
	const {
		item,
		userTimezone
	} = props;

	const {
		title,
		dateTime,
		subtitle1,
		subtitle2,
		subtitle3,
		description
	} = item;

	const titleValue: string | undefined = title?.value;

	const computedTitle: string = isNotNilOrWhiteSpace(titleValue)
		? titleValue
		: localize("_NoTitle");

	return (
		<ListItem
			trackingId={generateTrackingId("integration-live-tile-item", `|${item.uniqueId}`)}
		>
			<ListItem.Content
				{...item.link
					? {
						href: item.link,
						isTargetBlank: true,
					}
					: {}
				}
				tooltip={titleValue}
				thumbnail={
					dateTime
						? (
							<Thumbnail
								dateTime={dateTime.value}
								color={dateTime.color}
								userTimezone={userTimezone}
							/>
						)
						: undefined
				}
			>
				<ListItem.Title
					tooltip={computedTitle}
				>
					<ColoredText
						trackingId="title-text"
						color={title?.color}
					>
						{computedTitle}
					</ColoredText>
				</ListItem.Title>
				<Subtitles
					subTitle1={subtitle1}
					subTitle2={subtitle2}
					subTitle3={subtitle3}
				/>
				{description?.value && (
					<div className={styles.description}>
						<ColoredText
							trackingId="description"
							color={description.color}
						>
							{description.value}
						</ColoredText>
					</div>
				)}
			</ListItem.Content>
		</ListItem>
	);
};
Item.displayName = "Item";

type ThumbnailProps = {
	dateTime: string;
	color: ResultMapperColor | null;
	userTimezone: TimeZone;
};

const Thumbnail = (props: ThumbnailProps): ReactElement => {
	const {
		dateTime,
		color,
		userTimezone
	} = props;

	return (
		<div
			className={styles.thumbnail}
		>
			<h4>
				<ColoredText
					trackingId="thumbnail-date"
					color={color}
				>
					<DateString
						value={dateTime}
						format="DM"
						timezone={userTimezone}
					/>
				</ColoredText>
			</h4>
			<div className={styles.thumbnailTime}>
				<ColoredText
					trackingId="thumbnail-time"
					color={color}
				>
					<DateString
						value={dateTime}
						format="T"
						timezone={userTimezone}
					/>
				</ColoredText>
			</div>
		</div>
	);
};
Thumbnail.displayName = "Thumbnail";

type SubtitlesProps = {
	subTitle1: ColoredValueModel<string> | null;
	subTitle2: ColoredValueModel<string> | null;
	subTitle3: ColoredValueModel<string> | null;
};

const Subtitles = (props: SubtitlesProps): ReactElement | null => {
	const {
		subTitle1,
		subTitle2,
		subTitle3
	} = props;

	if (
		!subTitle1?.value
			&& !subTitle2?.value
			&& !subTitle3?.value
	) {
		return null;
	}

	return (
		<ListItem.Row>
			<div className={styles.subtitlesContainer}>
				{(subTitle1?.value || subTitle2?.value) && (
					<div className={styles.subtitlesLeftSide}>
						<ListItem.SubTitle>
							{subTitle1?.value && (
								<ColoredText
									trackingId="sub-title-1"
									color={subTitle1.color}
								>
									{subTitle1.value}
								</ColoredText>
							)}

							{subTitle1?.value && subTitle2?.value && (
								<PipeSeparator />
							)}

							{subTitle2?.value && (
								<ColoredText
									trackingId="sub-title-2"
									color={subTitle2.color}
								>
									{subTitle2.value}
								</ColoredText>
							)}
						</ListItem.SubTitle>
					</div>
				)}
				{subTitle3?.value && (
					<div className={styles.subtitlesRightSide}>
						<ListItem.SubTitle>
							<ColoredText
								trackingId="sub-title-3"
								color={subTitle3.color}
							>
								{subTitle3.value}
							</ColoredText>
						</ListItem.SubTitle>
					</div>
				)}
			</div>
		</ListItem.Row>
	);
};
Subtitles.displayName = "Subtitles";

type ColoredTextProps = {
	color: ResultMapperColor | nil;
	trackingId: string;
};

const ColoredText = (props: PropsWithChildren<ColoredTextProps>): ReactElement => {
	const {
		trackingId,
		color,
		children
	} = props;

	return (
		<span
			{...setTrackingId(trackingId)}
			className={
				isNotNil(color)
					? mappedColorToStyleMap[color]
					: undefined
			}
		>
			{children}
		</span>
	);
};
ColoredText.displayName = "ColoredText";

const mappedColorToStyleMap: Record<ResultMapperColor, string> = {
	[ResultMapperColor.Green]: styles.coloredText_success,
	[ResultMapperColor.Blue]: styles.coloredText_info,
	[ResultMapperColor.Orange]: styles.coloredText_warning,
	[ResultMapperColor.Red]: styles.coloredText_danger,
	[ResultMapperColor.Grey]: styles.coloredText_secondary,
	[ResultMapperColor.Default]: styles.coloredText_primary,
	[ResultMapperColor.Purple]: styles.coloredText_calendar6,
	[ResultMapperColor.Cyan]: styles.coloredText_calendar9
};
